import React, { useEffect, useMemo, useState } from 'react';
import { ExpandIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { copy } from '../data/copy';
import { galleryCategories, galleryItems } from '../data/gallery';
import { SectionHeading } from './SectionHeading';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import type { GalleryCategory, GalleryItem } from '../types';
type Filter = GalleryCategory | 'all';
export function Gallery() {
  const {
    t
  } = useLanguage();
  const [filter, setFilter] = useState<Filter>('all');
  const [active, setActive] = useState<GalleryItem | null>(null);
  const items = useMemo(() => filter === 'all' ? galleryItems : galleryItems.filter(item => item.category === filter), [filter]);
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(null);
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [active]);
  const filters: {
    id: Filter;
    label: string;
  }[] = [{
    id: 'all',
    label: t(copy.galleryAll)
  }, ...galleryCategories.map(category => ({
    id: category.id as Filter,
    label: t(category.label)
  }))];
  return <section id="gallery" className="bg-mist" data-id="element-190" data-mp="8doxy5fs3u15">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-id="element-191" data-mp="8f0buy2xui5z">
        <SectionHeading kicker={t(copy.galleryKicker)} title={t(copy.galleryTitle)} subtitle={t(copy.gallerySubtitle)} data-id="element-192" data-mp="dc07ef25w2iu" />

        <div className="mt-10 flex flex-wrap justify-center gap-2" role="tablist" aria-label={t(copy.galleryKicker)} data-id="element-193" data-mp="hgq3o50qfj4a">
          {filters.map(item => {
          const selected = filter === item.id;
          return <button key={item.id} type="button" role="tab" aria-selected={selected} onClick={() => setFilter(item.id)} className={`rounded-full px-4 py-2 font-display text-sm font-bold transition-colors duration-150 ease-out ${selected ? 'bg-royal text-white' : 'bg-white text-charcoal/70 hover:text-royal'}`} data-id="element-194" data-mp="1xs3tb8layf5m">
                {item.label}
              </button>;
        })}
        </div>

        <ul className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2" data-id="element-195" data-mp="366gx8a5rp197">
          {items.map(item => <li key={item.id} className="flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white p-3 shadow-card" data-id="element-196" data-mp="3kvd0zv0sjs40">
              <BeforeAfterSlider before={item.before} after={item.after} beforeLabel={t(copy.galleryBefore)} afterLabel={t(copy.galleryAfter)} alt={t(item.title)} ariaLabel={`${t(item.title)} — ${t(copy.gallerySliderLabel)}`} data-id="element-197" data-mp="2seivwlias9nl" />
              <div className="flex flex-1 flex-col px-2 pb-2 pt-4" data-id="element-198" data-mp="17p5d3gfnvyhw">
                <h3 className="font-display text-base font-extrabold text-navy" data-id="element-199" data-mp="txig1edwaly2">{t(item.title)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-charcoal/70" data-id="element-200" data-mp="xy2c0emqfjim">{t(item.caption)}</p>
                <div className="mt-auto flex items-center justify-between pt-4" data-id="element-201" data-mp="1lnmxb46cysv2">
                  <span className="text-xs font-medium text-charcoal/50" data-id="element-202" data-mp="1qn97t5usxv06">{t(copy.galleryDrag)}</span>
                  <button type="button" onClick={() => setActive(item)} className="flex items-center gap-1.5 font-display text-sm font-bold text-royal transition-colors duration-150 ease-out hover:text-bright" data-id="element-203" data-mp="1cngmjq21o6zl">
                    <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-204" data-mp="1anuee5cdgj0" />
                    {t(copy.galleryExpand)}
                  </button>
                </div>
              </div>
            </li>)}
        </ul>

        <div className="mx-auto mt-12 max-w-5xl rounded-3xl bg-royal px-7 py-10 text-center sm:px-12" data-id="element-205" data-mp="gpsxpt9rbbds">
          <h3 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl" data-id="element-206" data-mp="bqq1mybr8fhy">
            {t(copy.galleryCtaTitle)}
          </h3>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base" data-id="element-207" data-mp="7njrhfliu467">
            {t(copy.galleryCtaSubtitle)}
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-208" data-mp="zhvk7l92xlvy">
            <a href="#contact" className="w-full rounded-xl bg-bright px-6 py-3.5 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-royal sm:w-auto" data-id="element-209" data-mp="vefocr4u3pkb">
              {t(copy.galleryCtaPrimary)}
            </a>
            <a href="#services" className="w-full rounded-xl border-2 border-white/70 px-6 py-3.5 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-royal sm:w-auto" data-id="element-210" data-mp="3b5bmz3yv9695">
              {t(copy.galleryCtaSecondary)}
            </a>
          </div>
        </div>
      </div>

      {active ? <div role="dialog" aria-modal="true" aria-label={t(active.title)} className="fixed inset-0 z-50 flex items-center justify-center bg-navy/90 p-4" onClick={() => setActive(null)} data-id="element-211" data-mp="24fz5tgkbk6jz">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-4" onClick={event => event.stopPropagation()} data-id="element-212" data-mp="2efztfep818tz">
            <div className="mb-3 flex items-start justify-between gap-4" data-id="element-213" data-mp="2neuyuokfyew8">
              <div data-id="element-214" data-mp="oyysuika4kag">
                <h3 className="font-display text-lg font-extrabold text-navy" data-id="element-215" data-mp="ozqmhnubg7y3">{t(active.title)}</h3>
                <p className="mt-1 text-sm text-charcoal/70" data-id="element-216" data-mp="szse146c7kzn">{t(active.caption)}</p>
              </div>
              <button type="button" onClick={() => setActive(null)} aria-label={t(copy.galleryCloseLightbox)} className="rounded-lg p-2 text-charcoal transition-colors duration-150 ease-out hover:bg-mist" data-id="element-217" data-mp="1waoe5d6sgv3j">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-218" data-mp="21a1b4qrdjiju" />
              </button>
            </div>
            <BeforeAfterSlider before={active.before} after={active.after} beforeLabel={t(copy.galleryBefore)} afterLabel={t(copy.galleryAfter)} alt={t(active.title)} ariaLabel={`${t(active.title)} — ${t(copy.gallerySliderLabel)}`} data-id="element-219" data-mp="2f9sgy47qlsmr" />
          </div>
        </div> : null}
    </section>;
}